// src/pages/EcxLinkPage.tsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type LinkResponse = {
    ok?: boolean;
    tenantId?: string;
    workspace_public_id?: string;
    shopUrl?: string;

    accountId?: string | null;
    companyId?: string | null; // 互換で返る場合もある
    role?: string | null;
    status?: string | null;

    firebase_custom_token?: string;
    needsLink?: boolean;

    error?: string;
    message?: string;
    [k: string]: any;
};

export default function EcxLinkPage() {
    const navigate = useNavigate();
    const [shopUrl, setShopUrl] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const token = useMemo(() => localStorage.getItem("ecx_access_token") || "", []);
    const workspacePublicId = useMemo(
        () => (localStorage.getItem("ecx_workspace_public_id") || "").trim(),
        []
    );

    const normalizedHint = useMemo(() => {
        const s = shopUrl.trim();
        if (!s) return "";
        // ここはサーバ側で normalizeShopUrl されるので、ヒント表示だけ軽く
        return s
            .replace(/^https?:\/\//i, "")
            .replace(/^www\./i, "")
            .replace(/^.*rakuten\.co\.jp\//i, "")
            .replace(/^\/+|\/+$/g, "");
    }, [shopUrl]);

    const submit = async () => {
        if (!token) {
            console.log("★3")
            navigate("/ecx/login", { replace: true });
            return;
        }
        if (!shopUrl.trim()) {
            setError("shopUrl（あなたの楽天ショップのURL / ショップID）");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await fetch("/api/auth/ecx/link", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                    "x-tenant-id": "ecx",
                },
                body: JSON.stringify({
                    tenantId: "ecx",
                    shopUrl: shopUrl.trim(),
                }),
                cache: "no-store",
            });

            const json: LinkResponse = await res.json().catch(() => ({}));

            if (!res.ok) {
                console.error("[ecx link] failed:", res.status, json);

                // ショップが他テナントに登録済み → 該当テナントのログイン画面へ
                if (json?.error === "shop_registered_in_other_tenants") {
                    const targets: { tenantId: string; redirectPath: string }[] =
                        json?.redirectTargets ?? [];
                    const rp = (targets[0]?.redirectPath) || json?.redirectPath;
                    if (rp) {
                        navigate(rp, { replace: true });
                        return;
                    }
                }

                setError(json?.message || json?.error || "紐付けに失敗しました。入力内容をご確認ください。");
                setLoading(false);
                return;
            }

            // ✅ tenant 固定
            localStorage.setItem("selectedTenant", "ecx");

            // 参考情報保存（任意）
            if (json?.workspace_public_id) {
                localStorage.setItem("ecx_workspace_public_id", String(json.workspace_public_id));
            }

            // 互換：companyId/accountId が返ってきたら一応保存（check-status が最終確定する）
            const companyId = (json.companyId || json.accountId || "") as string;
            if (companyId) {
                localStorage.setItem("companyId", companyId);
                localStorage.setItem("accountId", companyId);
                localStorage.setItem("selectedCompanyId", companyId);
            }

            if (json.shopUrl) {
                localStorage.setItem("shopUrl", String(json.shopUrl));
            }

            if (json.plan) localStorage.setItem("plan", json.plan);
            else localStorage.removeItem("plan");

            if (json.trialStatus) localStorage.setItem("trialStatus", json.trialStatus);
            else localStorage.removeItem("trialStatus");

            // ✅ 次で確定判定
            navigate("/check-status", { replace: true });
        } catch (e) {
            console.error("[ecx link] error:", e);
            setError("通信エラーが発生しました。時間をおいて再度お試しください。");
            setLoading(false);
        }
    };

    const logout = () => {
        // ECX関係だけ掃除（必要最低限）
        localStorage.removeItem("ecx_access_token");
        localStorage.removeItem("ecx_token_expiry");
        // workspace_public_id は「固定したい」なら残す運用でもOKだが、迷うなら消してOK
        // localStorage.removeItem("ecx_workspace_public_id");
        localStorage.removeItem("selectedTenant");
        navigate("/ecx/login", { replace: true });
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100">
            <div className="w-full max-w-md px-4">
                <div className="mb-6 text-center">
                    <div className="flex items-center justify-center mt-4">
                        <img src="/logo.png" alt="利益Navi" className="h-16 rounded-xl object-cover" />
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                        初回ログインのため、楽天ショップURL（shopUrl）を紐付けしてください。
                    </p>
                    {/*workspacePublicId ? (
                        <p className="mt-1 text-[11px] text-slate-400">
                            workspace_public_id: {workspacePublicId}
                        </p>
                    ) : null*/}
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                    {error && (
                        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                            {error}
                        </div>
                    )}

                    {!token ? (
                        <>
                            <div className="text-sm text-slate-700">
                                ECXトークンが見つかりません。再度ECXログインしてください。
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate("/ecx/login", { replace: true })}
                                className="mt-4 w-full rounded-md bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-900"
                            >
                                ECXログインへ
                            </button>
                        </>
                    ) : (
                        <>
                            <label className="block text-xs font-semibold text-slate-700">
                                shopUrl（あなたの楽天ショップのURL / ショップID）
                            </label>
                            <input
                                value={shopUrl}
                                onChange={(e) => {
                                    setShopUrl(e.target.value);
                                    if (error) setError(null);
                                }}
                                placeholder="e-例）あなたのショップID（例: your-shop） または https://www.rakuten.co.jp/your-shop/"
                                className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                            />

                            {normalizedHint ? (
                                <p className="mt-2 text-[11px] text-slate-400">
                                    入力ヒント（正規化後イメージ）: {normalizedHint}
                                </p>
                            ) : null}

                            <button
                                type="button"
                                onClick={submit}
                                disabled={loading}
                                className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-80"
                            >
                                <span>{loading ? "紐付け中..." : "この店舗を紐付け"}</span>
                            </button>

                            <button
                                type="button"
                                onClick={logout}
                                disabled={loading}
                                className="mt-3 w-full rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-70"
                            >
                                ecxアカウントからやり直す
                            </button>

                            <p className="mt-4 text-[11px] text-slate-400">
                                ※ 紐付け後は自動的に利用可能な画面へ遷移します。
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
